import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Select from "react-select";
import toast from "react-hot-toast";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useAddRateMutation } from "../toolkitServices/rateApi";

const RateMaster = () => {
  const qParams = { page: 1, limit: 100 };
  const { data: categoryData = [], isLoading: catLoading } = useGetAllCategoriesQuery(qParams);
  const { data: purityData = [], isLoading: purLoading } = useGetAllPuritiesQuery(qParams);
  const [addRate, { isLoading: saveLoading }] = useAddRateMutation();

  // state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [baseRate, setBaseRate] = useState("");
  const [purityRates, setPurityRates] = useState([]);

  /* 🔹 when category or baseRate changes */
  useEffect(() => {
    if (!selectedCategory) {
      setPurityRates([]);
      return;
    }

    const filtered = purityData?.data?.filter(
      (p) => p.categoryId?._id === selectedCategory.value
    );

    setPurityRates(
      filtered.map((p) => ({
        ...p,
        calculatedRate: baseRate
          ? ((Number(baseRate) * Number(p.finePercentage)) / 100).toFixed(2)
          : "",
      }))
    );
  }, [selectedCategory, baseRate, purityData]);

  const handleBaseRateChange = (e) => setBaseRate(e.target.value);

  /* 🔹 Save API */
  const handleSave = async () => {
    if (!selectedCategory || !baseRate) {
      toast.error("Please select category & enter base rate!");
      return;
    }

    const payload = purityRates.map((p) => ({
      categoryId: selectedCategory.value,
      purityId: p._id,
      ratePerGram: p.calculatedRate,
      effectiveDate: new Date().toISOString().split("T")[0],
    }));

    try {
      const res = await addRate(payload).unwrap();
      toast.success("✅ Rates saved successfully!");
    } catch (err) {
      console.error("Save failed:", err);
      const msg = err?.data?.message || "Failed to save rates!";
      toast.error(msg);
    }
  };

  if (catLoading || purLoading)
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 text-sm md:text-base">
        <div className="animate-spin h-5 w-5 border-2 border-[#9481ce] border-t-transparent rounded-full mr-2"></div>
        Loading categories & purities...
      </div>
    );

  const categoryOptions = categoryData?.data?.map((c) => ({
    value: c._id,
    label: c.name,
  }));

  return (
    <div className="px-3 sm:px-5 py-4 sm:py-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-3 sm:px-5 py-2 sm:py-3 flex flex-wrap items-center gap-3">
          <Link
            to="/masters/daily-rate-list"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Rate List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              Rate Master
            </h1>
            <p className="text-xs sm:text-sm text-gray-500">
              Update daily base rates for each category. Purity rates auto-calculate.
            </p>
          </div>
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-6xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative overflow-hidden"
      >
        <div className="p-4 sm:p-6 space-y-6">
          {/* Row: Category + Base Rate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-[13px] sm:text-sm font-medium text-gray-700 mb-1">
                Category<span className="text-red-500">*</span>
              </label>
              <Select
                options={categoryOptions}
                value={selectedCategory}
                onChange={setSelectedCategory}
                placeholder="Select category..."
                className="text-sm"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderRadius: "0.5rem",
                    borderColor: state.isFocused ? "#9481ce" : "#d1d5db",
                    boxShadow: state.isFocused
                      ? "0 0 0 2px rgba(148,129,206,0.2)"
                      : "none",
                    minHeight: "38px",
                    // zIndex: 50,
                    position: "relative",
                  }),
                  menu: (base) => ({
                    ...base,
                    // zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.body}
              />
            </div>

            <div>
              <label className="block text-[13px] sm:text-sm font-medium text-gray-700 mb-1">
                Base Rate (per gram)<span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={baseRate}
                onChange={handleBaseRateChange}
                placeholder="e.g. 6400"
                className="w-full px-3 py-2.5 border rounded-lg text-[14px] border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
              />
            </div>
          </div>

          {/* Purity Table */}
          {purityRates.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="border rounded-xl overflow-auto max-h-[60vh] sm:max-h-none"
            >
              <table className="w-full text-xs sm:text-sm min-w-[480px]">
                <thead className="bg-[#f5f3ff] text-gray-800 sticky top-0">
                  <tr>
                    <th className="p-3 text-left font-semibold">Purity</th>
                    <th className="p-3 text-left font-semibold">Fine %</th>
                    <th className="p-3 text-left font-semibold">Calculated Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {purityRates.map((p, idx) => (
                    <motion.tr
                      key={p._id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="border-t hover:bg-[#faf9ff]"
                    >
                      <td className="p-3 text-gray-700">{p.purity}</td>
                      <td className="p-3 text-gray-700">{p.finePercentage}</td>
                      <td className="p-3 font-medium text-[#6f5cc5]">
                        {p.calculatedRate || "--"}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          )}

          {/* Save */}
          {purityRates.length > 0 && (
            <div className="flex justify-center sm:justify-end pt-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={saveLoading}
                onClick={handleSave}
                className={`inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg text-sm sm:text-base ${
                  saveLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#9481ce] hover:bg-[#7d6ec0]"
                } text-white font-medium shadow-sm transition`}
              >
                <FaSave className="text-sm" />
                {saveLoading ? "Saving..." : "Save Rates"}
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default RateMaster;
